import { Input } from "@/common/components/ui/input";
import { Label } from "@/common/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/common/components/ui/card";
import { Separator } from "@/common/components/ui/separator";
import { TabProps } from "../types";

interface CapitalTabProps extends TabProps {
  validationErrors?: Record<string, string>;
}

const CapitalTab = ({
  formData,
  handleInputChange,
  validationErrors = {},
  readOnly = false,
}: CapitalTabProps) => {
  const readOnlyClass = readOnly ? "bg-slate-50 cursor-not-allowed" : "";
  const formatIndianAmount = (value: string | number | null | undefined) => {
    const num = Number(value ?? 0);
    return Number.isNaN(num) ? "0" : num.toLocaleString("en-IN");
  };
  const formatIndianNumberInput = (
    value: string | number | null | undefined
  ) => {
    const raw = String(value ?? "").replace(/,/g, "");
    if (!raw || raw === "-" || raw === "." || raw === "-.") {
      return raw;
    }

    const [intPart, decimalPart] = raw.split(".");
    const isNegative = intPart.startsWith("-");
    const absInt = intPart.replace("-", "");
    if (!/^\d+$/.test(absInt)) {
      return String(value ?? "");
    }

    const grouped = Number(absInt).toLocaleString("en-IN");
    const withSign = isNegative ? `-${grouped}` : grouped;
    return decimalPart !== undefined ? `${withSign}.${decimalPart}` : withSign;
  };
  const buildNumericChangeHandler =
    (name: string, allowDecimal = false) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const normalized = e.target.value.replace(/,/g, "");
      const pattern = allowDecimal ? /^-?\d*\.?\d*$/ : /^\d*$/;

      if (
        normalized !== "" &&
        normalized !== "-" &&
        normalized !== "." &&
        !pattern.test(normalized)
      ) {
        return;
      }

      handleInputChange({
        ...e,
        target: {
          ...e.target,
          name,
          value: normalized,
        },
      } as React.ChangeEvent<HTMLInputElement>);
    };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
        {/* Shares Offered Section */}
        <div className="space-y-6 md:col-span-12">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
              <CardTitle className="text-sm font-bold text-slate-800">
                Shares Offered (Quantity)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 p-6">
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Fresh Issue
                  </Label>
                  <Input
                    name="fresh_issue"
                    type="text"
                    inputMode="decimal"
                    value={formatIndianNumberInput(formData.fresh_issue)}
                    onChange={buildNumericChangeHandler("fresh_issue", true)}
                    disabled={readOnly}
                    placeholder={formData.fresh_issue || "e.g. ₹118.00 Cr"}
                    min={0}
                    step="0.01"
                    className={`h-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                      validationErrors.fresh_issue
                        ? "border-red-300 focus:border-red-500"
                        : ""
                    }`}
                  />
                  {validationErrors.fresh_issue && (
                    <p className="text-xs text-red-600">
                      {validationErrors.fresh_issue}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Offer for Sale (OFS)
                  </Label>
                  <Input
                    name="ofs"
                    type="text"
                    inputMode="decimal"
                    value={formatIndianNumberInput(formData.ofs)}
                    onChange={buildNumericChangeHandler("ofs", true)}
                    disabled={readOnly}
                    placeholder={formData.ofs || "e.g. 0.00 Cr"}
                    min={0}
                    step="0.01"
                    className={`h-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                      validationErrors.ofs
                        ? "border-red-300 focus:border-red-500"
                        : ""
                    }`}
                  />
                  {validationErrors.ofs && (
                    <p className="text-xs text-red-600">
                      {validationErrors.ofs}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-bold uppercase text-slate-400">
                    QIB Shares
                  </Label>
                  <Input
                    name="qib_shares_offered"
                    type="text"
                    inputMode="numeric"
                    value={formatIndianNumberInput(formData.qib_shares_offered)}
                    onChange={buildNumericChangeHandler("qib_shares_offered")}
                    disabled={readOnly}
                    placeholder={formData.qib_shares_offered || "0"}
                    min={0}
                    className={`h-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                      validationErrors.qib_shares_offered
                        ? "border-red-300 focus:border-red-500"
                        : ""
                    }`}
                  />
                  {validationErrors.qib_shares_offered && (
                    <p className="text-[10px] text-red-600">
                      {validationErrors.qib_shares_offered}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-bold uppercase text-slate-400">
                    NII Shares
                  </Label>
                  <Input
                    name="nii_shares_offered"
                    type="text"
                    inputMode="numeric"
                    value={formatIndianNumberInput(formData.nii_shares_offered)}
                    onChange={buildNumericChangeHandler("nii_shares_offered")}
                    disabled={readOnly}
                    placeholder={formData.nii_shares_offered || "0"}
                    min={0}
                    className={`h-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                      validationErrors.nii_shares_offered
                        ? "border-red-300 focus:border-red-500"
                        : ""
                    }`}
                  />
                  {validationErrors.nii_shares_offered && (
                    <p className="text-[10px] text-red-600">
                      {validationErrors.nii_shares_offered}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-bold uppercase text-slate-400">
                    Retail Shares
                  </Label>
                  <Input
                    name="retail_shares_offered"
                    type="text"
                    inputMode="numeric"
                    value={formatIndianNumberInput(
                      formData.retail_shares_offered
                    )}
                    onChange={buildNumericChangeHandler(
                      "retail_shares_offered"
                    )}
                    disabled={readOnly}
                    placeholder={formData.retail_shares_offered || "0"}
                    min={0}
                    className={`h-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                      validationErrors.retail_shares_offered
                        ? "border-red-300 focus:border-red-500"
                        : ""
                    }`}
                  />
                  {validationErrors.retail_shares_offered && (
                    <p className="text-[10px] text-red-600">
                      {validationErrors.retail_shares_offered}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-bold uppercase text-slate-400">
                    Anchor Shares
                  </Label>
                  <Input
                    name="anchor_shares_offered"
                    type="text"
                    inputMode="numeric"
                    value={formatIndianNumberInput(
                      formData.anchor_shares_offered
                    )}
                    onChange={buildNumericChangeHandler(
                      "anchor_shares_offered"
                    )}
                    disabled={readOnly}
                    placeholder={formData.anchor_shares_offered || "0"}
                    min={0}
                    className={`h-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                      validationErrors.anchor_shares_offered
                        ? "border-red-300 focus:border-red-500"
                        : ""
                    }`}
                  />
                  {validationErrors.anchor_shares_offered && (
                    <p className="text-[10px] text-red-600">
                      {validationErrors.anchor_shares_offered}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-bold uppercase text-slate-400">
                    Employee Shares
                  </Label>
                  <Input
                    name="employee_shares_offered"
                    type="text"
                    inputMode="numeric"
                    value={formatIndianNumberInput(
                      formData.employee_shares_offered
                    )}
                    onChange={buildNumericChangeHandler(
                      "employee_shares_offered"
                    )}
                    disabled={readOnly}
                    placeholder={formData.employee_shares_offered || "0"}
                    min={0}
                    className={`h-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                      validationErrors.employee_shares_offered
                        ? "border-red-300 focus:border-red-500"
                        : ""
                    }`}
                  />
                  {validationErrors.employee_shares_offered && (
                    <p className="text-[10px] text-red-600">
                      {validationErrors.employee_shares_offered}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-bold uppercase text-slate-400">
                    Market Maker Shares
                  </Label>
                  <Input
                    name="market_maker_shares_offered"
                    type="text"
                    inputMode="numeric"
                    value={formatIndianNumberInput(
                      formData.market_maker_shares_offered
                    )}
                    onChange={buildNumericChangeHandler(
                      "market_maker_shares_offered"
                    )}
                    disabled={readOnly}
                    placeholder={formData.market_maker_shares_offered || "0"}
                    min={0}
                    className={`h-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                      validationErrors.market_maker_shares_offered
                        ? "border-red-300 focus:border-red-500"
                        : ""
                    }`}
                  />
                  {validationErrors.market_maker_shares_offered && (
                    <p className="text-[10px] text-red-600">
                      {validationErrors.market_maker_shares_offered}
                    </p>
                  )}
                </div>
              </div>
              <Separator />
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-[10px] font-bold uppercase text-slate-400">
                    Market Maker Portion
                  </Label>
                  <Input
                    name="market_maker_portion"
                    type="text"
                    inputMode="numeric"
                    value={formatIndianNumberInput(
                      formData.market_maker_portion
                    )}
                    onChange={buildNumericChangeHandler("market_maker_portion")}
                    disabled={readOnly}
                    placeholder={
                      formData.market_maker_portion || "e.g. 524,000 Shares"
                    }
                    className={`h-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass}`}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-bold uppercase text-slate-400">
                    Employee Discount (₹)
                  </Label>
                  <Input
                    name="employee_discount"
                    type="text"
                    inputMode="decimal"
                    value={formatIndianNumberInput(formData.employee_discount)}
                    onChange={buildNumericChangeHandler(
                      "employee_discount",
                      true
                    )}
                    disabled={readOnly}
                    placeholder={formData.employee_discount || "0.00"}
                    min={0}
                    step="0.01"
                    className={`h-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                      validationErrors.employee_discount
                        ? "border-red-300 focus:border-red-500"
                        : ""
                    }`}
                  />
                  {validationErrors.employee_discount && (
                    <p className="text-xs text-red-600">
                      {validationErrors.employee_discount}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Shares & Capital Structure */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
            <CardTitle className="text-sm font-bold text-slate-800">
              Shares & Capital Structure
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-6 p-6">
            <div className="space-y-2">
              <Label className="text-[10px] font-bold uppercase text-slate-400">
                Pre-Issue Shares
              </Label>
              <Input
                name="pre_issue_shares"
                type="text"
                inputMode="numeric"
                value={formatIndianNumberInput(formData.pre_issue_shares)}
                onChange={buildNumericChangeHandler("pre_issue_shares")}
                disabled={readOnly}
                placeholder={formData.pre_issue_shares || "0"}
                min={0}
                className={`h-10 font-mono placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                  validationErrors.pre_issue_shares
                    ? "border-red-300 focus:border-red-500"
                    : ""
                }`}
              />
              {validationErrors.pre_issue_shares && (
                <p className="text-xs text-red-600">
                  {validationErrors.pre_issue_shares}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-bold uppercase text-slate-400">
                Post-Issue Shares
              </Label>
              <Input
                name="post_issue_shares"
                type="text"
                inputMode="numeric"
                value={formatIndianNumberInput(formData.post_issue_shares)}
                onChange={buildNumericChangeHandler("post_issue_shares")}
                disabled={readOnly}
                placeholder={formData.post_issue_shares || "0"}
                min={0}
                className={`h-10 font-mono placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                  validationErrors.post_issue_shares
                    ? "border-red-300 focus:border-red-500"
                    : ""
                }`}
              />
              {validationErrors.post_issue_shares && (
                <p className="text-xs text-red-600">
                  {validationErrors.post_issue_shares}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-bold uppercase text-slate-400">
                Pre-Issue Holding (%)
              </Label>
              <Input
                name="pre_issue_holding"
                type="number"
                step="0.01"
                value={formData.pre_issue_holding}
                onChange={handleInputChange}
                disabled={readOnly}
                placeholder={formData.pre_issue_holding || "0.00"}
                min={0}
                max={100}
                className={`h-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                  validationErrors.pre_issue_holding
                    ? "border-red-300 focus:border-red-500"
                    : ""
                }`}
              />
              {validationErrors.pre_issue_holding && (
                <p className="text-xs text-red-600">
                  {validationErrors.pre_issue_holding}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-bold uppercase text-slate-400">
                Post-Issue Holding (%)
              </Label>
              <Input
                name="post_issue_holding"
                type="number"
                step="0.01"
                value={formData.post_issue_holding}
                onChange={handleInputChange}
                disabled={readOnly}
                placeholder={formData.post_issue_holding || "0.00"}
                min={0}
                max={100}
                className={`h-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                  validationErrors.post_issue_holding
                    ? "border-red-300 focus:border-red-500"
                    : ""
                }`}
              />
              {validationErrors.post_issue_holding && (
                <p className="text-xs text-red-600">
                  {validationErrors.post_issue_holding}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Investment Minimums (Moved from Allocation) */}
        <div className="h-fit space-y-4 rounded-xl border border-blue-100/50 bg-blue-50/50 p-6">
          <h4 className="mb-2 text-xs font-bold text-primary">
            Investment Minimums (Real-time)
          </h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-slate-500">
                Retail ({formData.retail_min_lot || 1}{" "}
                {Number(formData.retail_min_lot) > 1 ? "Lots" : "Lot"})
              </span>
              <div className="text-right">
                <div className="font-bold text-slate-800">
                  ₹{formatIndianAmount(formData.retail_min_amount)}
                </div>
                <div className="text-[10px] text-slate-400">
                  {formData.retail_min_shares || 0} Shares
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-slate-500">
                sNII ({formData.snii_lots || 14}{" "}
                {Number(formData.snii_lots) > 1 ? "Lots" : "Lot"})
              </span>
              <div className="text-right">
                <div className="font-bold text-slate-800">
                  ₹{formatIndianAmount(formData.snii_min_investment)}
                </div>
                <div className="text-[10px] text-slate-400">
                  {formData.snii_min_shares || 0} Shares
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-slate-500">
                bNII ({formData.bnii_lots || 67}{" "}
                {Number(formData.bnii_lots) > 1 ? "Lots" : "Lot"})
              </span>
              <div className="text-right">
                <div className="font-bold text-slate-800">
                  ₹{formatIndianAmount(formData.bnii_min_investment)}
                </div>
                <div className="text-[10px] text-slate-400">
                  {formData.bnii_min_shares || 0} Shares
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reservation & Allotment */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
          <CardTitle className="text-sm font-bold text-slate-800">
            Reservation & Allotment (%)
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-8 p-6 md:grid-cols-2">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-[10px] font-bold uppercase text-slate-400">
                  QIB Portion (%)
                </Label>
                <Input
                  name="reservations_qib"
                  type="number"
                  step="any"
                  value={formData.reservations_qib}
                  onChange={handleInputChange}
                  disabled={readOnly}
                  placeholder={formData.reservations_qib || "0.00"}
                  className={`h-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                    validationErrors.reservations_qib
                      ? "border-red-300 focus:border-red-500"
                      : ""
                  }`}
                />
                {validationErrors.reservations_qib && (
                  <p className="text-[10px] text-red-600">
                    {validationErrors.reservations_qib}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-bold uppercase text-slate-400">
                  NII Portion (%)
                </Label>
                <Input
                  name="reservations_nii"
                  type="number"
                  step="any"
                  value={formData.reservations_nii}
                  onChange={handleInputChange}
                  disabled={readOnly}
                  placeholder={formData.reservations_nii || "0.00"}
                  className={`h-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                    validationErrors.reservations_nii
                      ? "border-red-300 focus:border-red-500"
                      : ""
                  }`}
                />
                {validationErrors.reservations_nii && (
                  <p className="text-[10px] text-red-600">
                    {validationErrors.reservations_nii}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-bold uppercase text-slate-400">
                  Retail Portion (%)
                </Label>
                <Input
                  name="reservations_retail"
                  type="number"
                  step="any"
                  value={formData.reservations_retail}
                  onChange={handleInputChange}
                  disabled={readOnly}
                  placeholder={formData.reservations_retail || "0.00"}
                  className={`h-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                    validationErrors.reservations_retail
                      ? "border-red-300 focus:border-red-500"
                      : ""
                  }`}
                />
                {validationErrors.reservations_retail && (
                  <p className="text-[10px] text-red-600">
                    {validationErrors.reservations_retail}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-bold uppercase text-slate-400">
                  Employee Portion (%)
                </Label>
                <Input
                  name="reservations_employee"
                  type="number"
                  step="any"
                  value={formData.reservations_employee}
                  onChange={handleInputChange}
                  disabled={readOnly}
                  placeholder={formData.reservations_employee || "0.00"}
                  className={`h-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                    validationErrors.reservations_employee
                      ? "border-red-300 focus:border-red-500"
                      : ""
                  }`}
                />
                {validationErrors.reservations_employee && (
                  <p className="text-[10px] text-red-600">
                    {validationErrors.reservations_employee}
                  </p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CapitalTab;
